import React, { useContext } from 'react';
import ButtonTableAction from './buttons/ButtonTableAction';
import DataTable from 'react-data-table-component';
import LazyLoading from './LazyLoading';
import { momentParseDate } from '../helpers';
import { DocumentNameFilters } from '../app/models/Document';
import DocumentFilter from './DocumentFilter';
import { DocumentSet } from '../app/models/DocumentSet';
import { AuthContext } from '../contexts/AuthContext';

type Props = {
    data: any[];
    create?: (type: string) => void;
    showHistory?: (type: string) => void;
    edit?: (type: string) => void;
    destroy?: (id: number) => void;
    progressPending?: boolean;
    filter: DocumentNameFilters;
    setFilter: (filter: DocumentNameFilters) => void;
    onSearch: () => void;
    documentSets?: DocumentSet[];
    getDocumentSet?: (documentSetId: number) => void;
    documentType?: 'worker' | 'truck' | 'hopper' | 'society'; // Tipo de documento para permisos
};

const DocumentsDataTable = ({ 
    create, 
    data, 
    progressPending, 
    showHistory,
    edit,
    destroy,
    filter,
    setFilter,
    onSearch,
    documentSets,
    getDocumentSet,
    documentType = 'worker' // Por defecto worker para mantener compatibilidad
}: Props) => {
    const { auth } = useContext(AuthContext);

    // Mapeo de permisos según tipo de documento
    const permissionsMap = {
        worker: {
            upload: 'upload_workerdocument_file',
            view: 'view_workerdocument_file',
            change: 'change_workerdocument',
            delete: 'delete_workerdocument_file'
        },
        truck: {
            upload: 'upload_truckdocument_file',
            view: 'view_truckdocument_file',
            change: 'change_truckdocument',
            delete: 'delete_truckdocument_file'
        },
        hopper: {
            upload: 'upload_hopperdocument_file',
            view: 'view_hopperdocument_file',
            change: 'change_hopperdocument',
            delete: 'delete_hopperdocument_file'
        },
        society: {
            upload: 'upload_societydocument_file',
            view: 'view_societydocument_file',
            change: 'change_societydocument',
            delete: 'delete_societydocument_file'
        }
    };

    const permissions = permissionsMap[documentType];

    const columns = [
        {
            name: 'Documento',
            selector: (row: any) => row.name,
            sortable: false,
            sortField: 'documento',
            cell: (row: any, index: any, column: any) => (
                <span className="">{row.document_name}</span>
            )
        },
        {
            name: 'Nombre del Archivo',
            selector: (row: any) => row.name,
            cell: (row: any, index: any, column: any) => (
                <a
                    className="fw-bold"
                    href={String(row?.file)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {row?.name}
                </a>
            )
        },
        {
            name: 'Fecha de Emisión',
            selector: (row: any) => row.issue_date,
            cell: (row: any, index: any, column: any) => (
                <span className="">{momentParseDate(row?.issue_date)}</span>
            )
        },
        {
            name: 'Fecha de Vencimiento',
            selector: (row: any) => row.expiration_date,
            cell: (row: any, index: any, column: any) => (
                <span className="">{momentParseDate(row?.expiration_date)}</span>
            )
        },
        {
            name: 'Indefinido',
            selector: (row: any) => row.is_expire_date_indefinite,
            cell: (row: any, index: any, column: any) => (
                <div className="">
                    {row.is_expire_date_indefinite ? <span>Sí</span> : <span>No</span>}
                </div>
            )
        },
        {
            name: 'Acciones',
            selector: (row: any) => row.id,
            sortable: false,
            cell: (row: any, index: any, column: any) => (
                <div className="">
                    {create && (auth?.permissions?.includes(permissions.upload) || row?.can_upload_without_role) && (
                        <ButtonTableAction
                            callbackFunction={() => create(row?.id ?? -1)}
                            classIcon={'mdi mdi-file-plus'}
                            colorIcon={'text-success'}
                            errorMessage={'No se puede mostrar este registro.'}
                            title={'Cargar'}
                        />
                    )}
                    {showHistory && (auth?.permissions?.includes(permissions.view) || row?.can_view_without_role) && (
                        <ButtonTableAction
                            callbackFunction={() => showHistory(row?.id ?? -1)}
                            classIcon={'mdi mdi-eye'}
                            colorIcon={'text-info'}
                            errorMessage={'No se puede mostrar este registro.'}
                            title={'Ver'}
                        />
                    )}
                    {edit && row.file && (auth?.permissions?.includes(permissions.change)) && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row?.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (auth?.permissions?.includes(permissions.delete) || row?.can_delete_without_role) && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <div>
            <div className="row mt-3">
                <div className="col">
                    <DocumentFilter 
                        filter={filter} 
                        setFilter={setFilter} 
                        onSearch={onSearch}
                        documentSets={documentSets ?? []}
                        getDocumentSet={getDocumentSet}
                    />
                </div>
            </div>
            <DataTable
                columns={columns}
                data={data}
                progressPending={progressPending}
                progressComponent={<LazyLoading height={300} />}
                noDataComponent="No hay documentos registrados"
            />
        </div>
    );
};

export default DocumentsDataTable;
